/* ============================================================================
   Sign-in — Direction C "UVA Co-Brand".
   Split layout: "The Drafting Room" brand panel (self-drawing Rotunda
   blueprint over a navy aurora + drafting grid, kinetic headline, looping
   Brief -> Design -> Proof -> Deliver strip) on the left, email/password form
   on the right. No MFA (v1). Users are admin-provisioned — there is
   intentionally no sign-up link, only a "Request access" dialog that files a
   request for an administrator to review.
   ============================================================================ */

import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { isApiError } from '../../lib/api';
import RequestAccessDialog from './RequestAccessDialog';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import './login.css';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      {/* ===================== LEFT: "The Drafting Room" brand panel ===================== */}
      <aside className="brand" aria-label="UVA Creative Requests">
        {/* ambient layers */}
        <div className="aurora" aria-hidden="true">
          <i className="a1" />
          <i className="a2" />
          <i className="a3" />
        </div>
        <div className="grid-paper" aria-hidden="true" />

        {/* floating survey accents */}
        <svg className="float f1" width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
          <circle cx="13" cy="13" r="10" fill="none" stroke="var(--uva-orange-400)" strokeWidth="1.5" />
          <circle cx="13" cy="13" r="2" fill="var(--uva-orange-400)" />
        </svg>
        <svg className="float f2" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <rect
            x="3"
            y="3"
            width="12"
            height="12"
            fill="none"
            stroke="var(--uva-orange-400)"
            strokeWidth="1.5"
            transform="rotate(18 9 9)"
          />
        </svg>
        <svg className="float f3" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 2v16M2 10h16" stroke="var(--uva-orange-400)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <svg className="float f4" width="30" height="14" viewBox="0 0 30 14" aria-hidden="true">
          <path
            d="M2 12L15 2l13 10"
            fill="none"
            stroke="var(--uva-orange-400)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>

        {/* top rule */}
        <div className="top-line">UVA Health · InterScripts</div>

        {/* hero */}
        <div className="hero">
          {/* Rotunda blueprint, draws itself on load */}
          <svg
            className="rotunda-draw"
            viewBox="0 0 320 310"
            role="img"
            aria-label="UVA Rotunda, drawn as blueprint line art"
          >
            {/* soft wash behind the drawing */}
            <g className="wash">
              <path d="M78 136a82 82 0 0 1 164 0v0H78z" fill="rgba(244,245,248,0.05)" />
              <rect x="78" y="148" width="164" height="104" fill="rgba(244,245,248,0.04)" />
            </g>

            {/* steps */}
            <path className="ln s3" pathLength="1" d="M46 290h228" />
            <path className="ln s2" pathLength="1" d="M58 276h204" />
            <path className="ln s1" pathLength="1" d="M70 262h180" />
            {/* stylobate */}
            <path className="ln s1 thin" pathLength="1" d="M78 252h164" />

            {/* colonnade */}
            <path className="ln c1" pathLength="1" d="M94 252V150" />
            <path className="ln c2" pathLength="1" d="M120 252V150" />
            <path className="ln c3" pathLength="1" d="M146 252V150" />
            <path className="ln c4" pathLength="1" d="M174 252V150" />
            <path className="ln c5" pathLength="1" d="M200 252V150" />
            <path className="ln c6" pathLength="1" d="M226 252V150" />

            {/* entablature */}
            <path className="ln e1" pathLength="1" d="M76 148h168" />
            <path className="ln e2 orange" pathLength="1" d="M76 138h168" />

            {/* dome + inner arc */}
            <path className="ln d1" pathLength="1" d="M78 136a82 82 0 0 1 164 0" />
            <path className="ln d2 thin" pathLength="1" d="M102 136a58 58 0 0 1 116 0" />

            {/* oculus */}
            <circle className="oculus" cx="160" cy="46" r="6" />

            {/* measure line + ticks */}
            <path className="ln m1 orange thin" pathLength="1" d="M46 304h228" />
            <path className="tick t1" d="M46 299v10" />
            <path className="tick t2" d="M122 300v8" />
            <path className="tick t3" d="M198 300v8" />
            <path className="tick t4" d="M274 299v10" />
          </svg>

          <div>
            <h1 className="display" aria-label="Creative Requests">
              <span className="wline w1" aria-hidden="true">
                <span>Creative</span>
              </span>
              <span className="wline w2" aria-hidden="true">
                <span>
                  Requests<span className="accent-word">.</span>
                </span>
              </span>
            </h1>
            <p className="sub">
              Submit, track, and deliver design work for UVA Health — from first brief to final
              proof, all in one place.
            </p>
          </div>

          {/* looping workflow spotlight */}
          <div className="flow" aria-hidden="true">
            <span className="step">Brief</span>
            <span className="sep">/</span>
            <span className="step">Design</span>
            <span className="sep">/</span>
            <span className="step">Proof</span>
            <span className="sep">/</span>
            <span className="step">Deliver</span>
          </div>
        </div>

        {/* footer */}
        <div className="foot">&copy; {new Date().getFullYear()} InterScripts · built for UVA Health</div>

        <div className="base-bar" aria-hidden="true" />
      </aside>

      {/* ===================== RIGHT: existing form, unchanged ===================== */}
      <section className="form-panel">
        <div className="inner">
          <p className="eyebrow">Sign in</p>
          <h2 style={{ marginBottom: 6 }}>Welcome back</h2>
          <p className="muted" style={{ marginBottom: 28 }}>
            Use your UVA Creative Requests account.
          </p>

          <form onSubmit={onSubmit} noValidate>
            <div className={`field ${error ? 'field-error' : ''}`}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="in"
                type="email"
                autoComplete="username"
                placeholder="you@uvahealth.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className={`field ${error ? 'field-error' : ''}`}>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                className="in"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 14 }}>
              <button
                type="button"
                className="request-access-link"
                onClick={() => setForgotPasswordOpen(true)}
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <p className="error-text" role="alert" style={{ marginTop: -6, marginBottom: 14 }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy || !email || !password}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="meta" style={{ marginTop: 24, textAlign: 'center' }}>
            Accounts are provisioned by an administrator. Need access?{' '}
            <button
              type="button"
              className="request-access-link"
              onClick={() => setRequestAccessOpen(true)}
            >
              Request access
            </button>
            .
          </p>
        </div>
      </section>

      {requestAccessOpen && <RequestAccessDialog onClose={() => setRequestAccessOpen(false)} />}
      {forgotPasswordOpen && <ForgotPasswordDialog onClose={() => setForgotPasswordOpen(false)} />}
    </div>
  );
}
